/**
 * Purchase Orders Extensions Client - Pure Composition Pattern
 *
 * Uses PurchaseOrdersClient for all HTTP infrastructure
 * Adds business logic extensions without inheritance
 */
import type { QueryClient } from '@tanstack/react-query';

import { PurchaseOrdersClient } from '../generated_clients/generated-purchase_orders-api-client.js';
import { purchaseOrdersKeys } from '../generated_hooks/generated-purchase_orders-hooks.js';
import type {
  PurchaseOrders,
  PurchaseOrdersInsert,
  PurchaseOrdersUpdate,
  PurchaseOrdersQueryOptions
} from '../generated_schemas/generated-purchase_orders-schema.js';

/**
 * Matches the HTTP client's "HTTP 404: ..." error shape thrown by the
 * generated PO client on record-not-found responses. Kept as a substring
 * check because the http-client wraps everything as a plain Error without
 * a status code field — see client/api/http-client.ts.
 */
export function isPurchaseOrderNotFoundError(err: unknown): boolean {
  if (!err) {return false;}
  const msg = err instanceof Error ? err.message : String(err);
  return msg.includes('HTTP 404');
}

/**
 * PO detail queries are invalidated by a wide net of mutations (status
 * changes, line-item receives, wizard step transitions) that use
 * purchaseOrdersKeys.all / .details() prefixes. If any of those refetches
 * targets a PO id the current org can no longer see (deleted, org
 * reassignment, stale wizard state), react-query's default retry policy
 * hammers a legitimate 404 twice and pollutes the console with a
 * misleading "🚨 HTTP Client Error" — the exact class of bug reported.
 *
 * This installs a targeted default for the detail-key prefix so 404s
 * fail fast without retries. Downstream components already treat
 * `data === undefined` as "PO not found" (see PurchaseOrderDetails), so
 * the UX gracefully falls back to the not-found alert instead of an
 * infinite loading state.
 */
export function configurePurchaseOrdersQueryDefaults(queryClient: QueryClient): void {
  queryClient.setQueryDefaults(purchaseOrdersKeys.details(), {
    retry: (failureCount: number, error: unknown) => {
      if (isPurchaseOrderNotFoundError(error)) {return false;}
      return failureCount < 1;
    },
  });
}

/**
 * Pure composition - no inheritance conflicts
 */
export class PurchaseOrdersExtensionsClient {
  constructor(private purchaseOrdersClient: PurchaseOrdersClient) {}

  // Delegate all basic operations to PurchaseOrdersClient
  async getAll(params?: PurchaseOrdersQueryOptions): Promise<PurchaseOrders[]> {
    return this.purchaseOrdersClient.getAll(params);
  }

  async getById(id: string): Promise<PurchaseOrders> {
    return this.purchaseOrdersClient.getById(id);
  }

  /**
   * Same as getById but returns null on HTTP 404 instead of throwing.
   * Use from callers that legitimately expect a PO id may no longer exist
   * (e.g. wizard state referencing a deleted draft, post-mutation
   * refetches on stale ids) — keeps the console clean and gives callers
   * a nullable to branch on.
   */
  async getByIdOrNull(id: string): Promise<PurchaseOrders | null> {
    try {
      return await this.purchaseOrdersClient.getById(id);
    } catch (err) {
      if (isPurchaseOrderNotFoundError(err)) {return null;}
      throw err;
    }
  }

  async create(data: PurchaseOrdersInsert): Promise<PurchaseOrders> {
    return this.purchaseOrdersClient.create(data);
  }

  async update(id: string, data: PurchaseOrdersUpdate): Promise<PurchaseOrders> {
    return this.purchaseOrdersClient.update(id, data);
  }

  async delete(id: string): Promise<void> {
    return this.purchaseOrdersClient.delete(id);
  }

  async count(filter?: Record<string, any>): Promise<number> {
    return this.purchaseOrdersClient.count(filter);
  }

  async exists(id: string): Promise<boolean> {
    return this.purchaseOrdersClient.exists(id);
  }

  // Logging utility
  private log(message: string, data?: any): void {
    console.log(`[PurchaseOrdersExtensions] ${message}`, data || '');
  }
}

// Factory function
export const createPurchaseOrdersExtensions = (purchaseOrdersClient: PurchaseOrdersClient): PurchaseOrdersExtensionsClient =>
  new PurchaseOrdersExtensionsClient(purchaseOrdersClient);

// Export singleton
const purchaseOrdersClient = new PurchaseOrdersClient();
export const purchaseOrdersExtensions = createPurchaseOrdersExtensions(purchaseOrdersClient);
